import * as THREE from "three";
import { VfxDissolvingSphereBatch } from "../../core/batches/vfx_dissolving_sphere_batch.js";
import { VfxErodingBillboardAdditiveBatch } from "../../core/batches/vfx_eroding_billboard_batch.js";
import { VfxRadialStreakBatch } from "../../core/batches/vfx_radial_streak_batch.js";
import { randomRange } from "../../../common/common_funcs.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { VfxGradientAtlas } from "../../core/textures/vfx_gradient_atlas.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";

export class VfxMineExplosionTask implements IVfxTask, IController
{
  public is_active: boolean = true;
  private static streaks_gradient_id: number = -1;
  private static smog_gradient_id: number    = -1;

  private position: THREE.Vector3;
  private start_time: number;
  private has_spawned: boolean = false;

  // --- Explosion Blast Constants (3D Sphere) ---
  private static readonly BLAST_NOISE_RES_ID           : ResourceID    = ResourceID.VornoiNoiseTexture;
  private static readonly BLAST_START_SCALE            : number        = 0.6;
  private static readonly BLAST_END_SCALE              : number        = 1.4;
  private static readonly BLAST_LIFESPAN               : number        = 0.4;
  private static readonly BLAST_DISSOLVE_START_VALUE   : number        = 0.5;
  private static readonly BLAST_DISSOLVE_END_VALUE     : number        = 1;
  private static readonly BLAST_DISSOLVE_START_PROGRESS: number        = 0;
  private static readonly BLAST_FLOW_DIRECTION         : THREE.Vector3 = new THREE.Vector3( 1, 1, 1 );
  private static readonly BLAST_FLOW_SPEED             : number        = 1.5;
  private static readonly BLAST_COLOR_1                : THREE.Color   = new THREE.Color( 0x64DCFF );
  private static readonly BLAST_COLOR_2                : THREE.Color   = new THREE.Color( 0x1E64FF );

  // --- Explosion Smog Constants (VfxErodingBillboardAdditiveBatch) ---
  private static readonly SMOG_BURST_COUNT            : number      = 10;
  private static readonly SMOG_LIFESPAN               : number      = 0.5;
  private static readonly SMOG_SPAWN_RADIUS           : number      = 0.6;
  private static readonly SMOG_START_SCALE            : number      = 0.4;
  private static readonly SMOG_END_SCALE              : number      = 2;
  private static readonly SMOG_SPEED                  : number      = 1.4;
  private static readonly SMOG_DISSOLVE_START_PROGRESS: number      = 0;
  private static readonly SMOG_DISSOLVE_SOFTNESS      : number      = 0.6;
  private static readonly SMOG_COLOR_1                : THREE.Color = new THREE.Color( 0x64DCFF );
  private static readonly SMOG_COLOR_2                : THREE.Color = new THREE.Color( 0x1E64FF );

  // --- 3D Radial Streaks Constants (VfxRadialStreakBatch + ShockwaveGlow4/5) ---
  private static readonly STREAKS_BURST_COUNT                   : number      = 20;
  private static readonly STREAKS_LIFESPAN                      : number      = 0.4;
  private static readonly STREAKS_SPEED                         : number      = 5;
  private static readonly STREAKS_MIN_SPAWN_AREA_RADIUS         : number      = 0;
  private static readonly STREAKS_MAX_SPAWN_AREA_RADIUS         : number      = 0.4;
  private static readonly STREAKS_MIN_WIDTH                     : number      = 1.6;
  private static readonly STREAKS_MAX_WIDTH                     : number      = 2.4;
  private static readonly STREAKS_MIN_HEIGHT                    : number      = 2;
  private static readonly STREAKS_MAX_HEIGHT                    : number      = 2.6;
  private static readonly STREAKS_BRIGHTNESS_FADE_START_PROGRESS: number      = 0.2;
  private static readonly STREAKS_DISSOLVE_START_PROGRESS       : number      = 0.1;
  private static readonly STREAKS_DISSOLVE_DIRECTION_WEIGHT     : number      = 0.6;
  private static readonly STREAKS_DISSOLVE_SOFTNESS             : number      = 0.15;
  private static readonly STREAKS_COLOR_1                       : THREE.Color = new THREE.Color( 0xB4F0FF ); // Ядро (яскравий біло-блакитний)
  private static readonly STREAKS_COLOR_2                       : THREE.Color = new THREE.Color( 0x3CA0FF ); // Середина (блакитно-синій)
  private static readonly STREAKS_COLOR_3                       : THREE.Color = new THREE.Color( 0x1450FF ); // Кромка (глибокий синій)

  private static readonly TOTAL_LIFESPAN: number = Math.max(
    VfxMineExplosionTask.BLAST_LIFESPAN,
    VfxMineExplosionTask.SMOG_LIFESPAN,
    VfxMineExplosionTask.STREAKS_LIFESPAN
  );

  public constructor( position: THREE.Vector3 )
  {
    this.position   = position.clone();
    this.start_time = Vfx3DRuntime.current_time;
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;
    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      this.spawnSphere();
      this.spawnSmog();
      this.spawnStreaks();
    }

    if ( age > VfxMineExplosionTask.TOTAL_LIFESPAN )
      return true;

    return false;
  }

  private static readonly scratch_dir = new THREE.Vector3();
  private static readonly scratch_pos = new THREE.Vector3();
  private static readonly scratch_vel = new THREE.Vector3();

  private spawnSphere(): void
  {
    const sphere_batch = Vfx3DRuntime.getBatch( VfxDissolvingSphereBatch );
    sphere_batch.spawnSphere(
      VfxMineExplosionTask.BLAST_NOISE_RES_ID,
      this.position,
      VfxMineExplosionTask.BLAST_START_SCALE,
      VfxMineExplosionTask.BLAST_END_SCALE,
      this.start_time,
      VfxMineExplosionTask.BLAST_LIFESPAN,
      VfxMineExplosionTask.BLAST_DISSOLVE_START_VALUE,
      VfxMineExplosionTask.BLAST_DISSOLVE_END_VALUE,
      VfxMineExplosionTask.BLAST_DISSOLVE_START_PROGRESS,
      VfxMineExplosionTask.BLAST_FLOW_DIRECTION,
      VfxMineExplosionTask.BLAST_FLOW_SPEED,
      undefined,
      VfxMineExplosionTask.BLAST_COLOR_1,
      VfxMineExplosionTask.BLAST_COLOR_2
    );
  }

  private spawnSmog(): void
  {
    const smog_batch = Vfx3DRuntime.getBatch( VfxErodingBillboardAdditiveBatch );

    if ( VfxMineExplosionTask.smog_gradient_id === -1 )
    {
      VfxMineExplosionTask.smog_gradient_id = VfxGradientAtlas.registerGradient( [
        { stop: 0.0, color: VfxMineExplosionTask.SMOG_COLOR_1 },
        { stop: 0.6, color: VfxMineExplosionTask.SMOG_COLOR_2 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) },
      ] );
    }

    const count = VfxMineExplosionTask.SMOG_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const u = Math.random() * 2.0 - 1.0;
      const theta = Math.random() * Math.PI * 2.0;
      const r = Math.sqrt( Math.max( 0.0, 1.0 - u * u ) );

      VfxMineExplosionTask.scratch_dir.set(
        r * Math.cos( theta ),
        u,
        r * Math.sin( theta )
      );

      const dist = Math.cbrt( Math.random() ) * VfxMineExplosionTask.SMOG_SPAWN_RADIUS;
      VfxMineExplosionTask.scratch_pos
        .copy( this.position )
        .addScaledVector( VfxMineExplosionTask.scratch_dir, dist );
      VfxMineExplosionTask.scratch_vel
        .copy( VfxMineExplosionTask.scratch_dir )
        .multiplyScalar( VfxMineExplosionTask.SMOG_SPEED );
      const rotation = Math.random() * Math.PI * 2;

      smog_batch.spawnParticle(
        ResourceID.Smog,
        ResourceID.CloudNoiseTexture,
        VfxMineExplosionTask.smog_gradient_id,
        VfxMineExplosionTask.scratch_pos,
        VfxMineExplosionTask.scratch_vel,
        VfxMineExplosionTask.SMOG_START_SCALE,
        VfxMineExplosionTask.SMOG_END_SCALE,
        this.start_time,
        VfxMineExplosionTask.SMOG_LIFESPAN,
        VfxMineExplosionTask.SMOG_DISSOLVE_START_PROGRESS,
        VfxMineExplosionTask.SMOG_DISSOLVE_SOFTNESS,
        rotation
      );
    }
  }

  private spawnStreaks(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxRadialStreakBatch );

    if ( VfxMineExplosionTask.streaks_gradient_id === -1 )
    {
      VfxMineExplosionTask.streaks_gradient_id = VfxGradientAtlas.registerGradient( [
        { stop: 0.2, color: VfxMineExplosionTask.STREAKS_COLOR_3 },
        { stop: 0.5, color: VfxMineExplosionTask.STREAKS_COLOR_2 },
        { stop: 1.0, color: VfxMineExplosionTask.STREAKS_COLOR_1 },
      ] );
    }

    const count = VfxMineExplosionTask.STREAKS_BURST_COUNT;
    const golden_angle = Math.PI * ( 3.0 - Math.sqrt( 5.0 ) );
    const random_rot = Math.random() * Math.PI * 2.0;

    for ( let i = 0; i < count; i++ )
    {
      const y = 1.0 - ( ( i + 0.5 ) / count ) * 2.0;
      const radius = Math.sqrt( Math.max( 0.0, 1.0 - y * y ) );
      const phi = i * golden_angle + random_rot;

      VfxMineExplosionTask.scratch_dir.set(
        Math.cos( phi ) * radius + randomRange( -0.15, 0.15 ),
        y + randomRange( -0.15, 0.15 ),
        Math.sin( phi ) * radius + randomRange( -0.15, 0.15 )
      ).normalize();

      const spawn_dist = randomRange(
        VfxMineExplosionTask.STREAKS_MIN_SPAWN_AREA_RADIUS,
        VfxMineExplosionTask.STREAKS_MAX_SPAWN_AREA_RADIUS
      );

      VfxMineExplosionTask.scratch_pos
        .copy( this.position )
        .addScaledVector( VfxMineExplosionTask.scratch_dir, spawn_dist );

      VfxMineExplosionTask.scratch_vel
        .copy( VfxMineExplosionTask.scratch_dir )
        .multiplyScalar( VfxMineExplosionTask.STREAKS_SPEED );

      const width  = randomRange( VfxMineExplosionTask.STREAKS_MIN_WIDTH, VfxMineExplosionTask.STREAKS_MAX_WIDTH );
      const height = randomRange( VfxMineExplosionTask.STREAKS_MIN_HEIGHT, VfxMineExplosionTask.STREAKS_MAX_HEIGHT );
      const res_id = Math.random() < 0.5 ? ResourceID.ShockwaveGlow4 : ResourceID.ShockwaveGlow5;

      batch.spawnParticle(
        res_id,
        VfxMineExplosionTask.streaks_gradient_id,
        VfxMineExplosionTask.scratch_pos,
        VfxMineExplosionTask.scratch_vel,
        width,
        height,
        VfxMineExplosionTask.STREAKS_BRIGHTNESS_FADE_START_PROGRESS,
        VfxMineExplosionTask.STREAKS_DISSOLVE_START_PROGRESS,
        this.start_time,
        VfxMineExplosionTask.STREAKS_LIFESPAN,
        1,
        VfxMineExplosionTask.STREAKS_DISSOLVE_DIRECTION_WEIGHT,
        VfxMineExplosionTask.STREAKS_DISSOLVE_SOFTNESS
      );
    }
  }
}
